// The relationship chart: how two people are related, as a document.
//
// This is the shape genealogy settled on long before software - FamilySearch's
// "View Relationship", every printed cousin chart, every parish clerk's working
// - and it is the right one: the common ancestor at the top, the two lines of
// descent side by side beneath, meeting at the two people the question was
// about. The relationship NAME is a headline rather than a puzzle, and the
// evidence for it is the two columns under it.
//
// It answers what the traced path on the chart cannot. A lit chain across a
// hundred and thirty-nine people is proof, but it is proof you have to pan
// around to read, and on a phone it is unreadable. This is a page: no zoom, no
// canvas, screenshot-able, and it prints.
//
// Rendered from the SAME ?path= pair as the trace, so a link is one thing with
// two ways of looking at it.
import { lifespanLabel } from "./genealogy"

export function renderRelationship(ctx) {
    let wrap = document.createElement("div")
    wrap.className = "ft-rel"

    let data = ctx.relationship
    if (!data) {
        wrap.appendChild(note(ctx.pathPair ? "Working out how they're related…" : "Pick two people to see how they're related."))
        return { element: wrap, panZoom: false }
    }
    if (!data.ok) {
        wrap.appendChild(note(data.error || "They aren't connected in this tree."))
        return { element: wrap, panZoom: false }
    }

    let path = data.path || []
    // Two families joined by marriage have no common ancestor to fork from:
    // each family's line climbs to its own ancestor, and marriages join the
    // two. A different shape, drawn from the same answer - see familyChart.
    if (data.cross && !data.label && data.marriages && data.from_ancestor && data.to_ancestor) {
        wrap.classList.add("ft-rel--families")
        wrap.appendChild(heading(data, false, ctx, null))
        let chart = familyChart(path, ctx)
        wrap.appendChild(chart.element)
        return { element: wrap, panZoom: false, cleanup: chart.cleanup }
    }
    let pivotIndex = data.via ? path.findIndex((p) => p.id === data.via.id) : -1
    // No common ancestor on the path means this is a straight line of descent
    // (a grandmother and her grandson), which is a chart with ONE column.
    let single = pivotIndex <= 0 || pivotIndex >= path.length - 1

    wrap.appendChild(heading(data, single, ctx, generations(path, pivotIndex, single)))
    wrap.appendChild(single ? oneLine(path, ctx) : twoLines(path, pivotIndex, ctx, data))
    return { element: wrap, panZoom: false }
}

// How far each line runs from the ancestor. "Nine generations each" is the
// fact that makes an eighth cousin mean something - the ordinal alone is a
// word, and this is the distance behind it.
function generations(path, pivotIndex, single) {
    if (single) return null

    let left = pivotIndex
    let right = path.length - 1 - pivotIndex
    if (!left || !right) return null

    return left === right
        ? `${spell(left)} generations on each side`
        : `${spell(left)} generations on one side, ${spell(right)} on the other`
}

const NUMBER_WORDS = ["", "one", "two", "three", "four", "five", "six", "seven", "eight", "nine", "ten", "eleven", "twelve"]
const spell = (n) => NUMBER_WORDS[n] || String(n)

function heading(data, single, ctx, generationLine) {
    let head = document.createElement("header")
    head.className = "ft-rel-head"

    // Who, through whom, therefore what - in that order, because that is the
    // order the news arrives in.
    //
    // "Half eighth cousins" led this page for a while and it is the wrong
    // headline: a reader opening a link like this has just learned that they
    // are related to someone, and the relationship TERM is a taxonomy label,
    // not the news. The two names are the news; the shared ancestor is the
    // payload; the label is the conclusion the other two build to. Leading
    // with the label asks a stranger to care about a classification before
    // they have been told who it classifies.
    let title = document.createElement("h2")
    title.className = "ft-rel-title"
    title.textContent = `${data.from.name} and ${data.to.name}`
    head.appendChild(title)

    if (data.cross && !data.label && data.from_ancestor && data.to_ancestor) {
        let via = document.createElement("p")
        via.className = "ft-rel-via"
        via.textContent = `Linked through ${named(data.from_ancestor)} and ${named(data.to_ancestor)}.`
        head.appendChild(via)
    } else if (!single && data.via) {
        let via = document.createElement("p")
        via.className = "ft-rel-via"
        // Label and value, not a sentence. "Their common ancestor is X" reads
        // as prose the eye has to parse; "Common ancestor: X" is a fact you
        // can take in without reading it.
        via.textContent = `Common ancestor: ${data.via.name}${data.via.lifespan ? `, ${data.via.lifespan}` : ""}.`
        head.appendChild(via)
    }

    // The answer itself, landing last and carrying the weight. The relationship
    // reads as a statement about the pair, not as a label on one of them:
    // "Eighth cousins", not "Eighth cousin".
    let verdict = document.createElement("p")
    verdict.className = "ft-rel-verdict"
    let between = data.marriages_between ?? data.marriages
    let label = data.label ? plural(data.label) : data.cross && between ? `Related by marriage · ${spell(between)} ${between === 1 ? "marriage" : "marriages"}` : "Related"
    verdict.textContent = generationLine ? `${label} · ${generationLine}` : label
    head.appendChild(verdict)

    // The floating lens bar is suppressed for this view, so its two actions
    // live here - on the document they belong to.
    let actions = document.createElement("div")
    actions.className = "ft-rel-actions"
    // The link that works for whoever you are sending it to - see
    // contact_tree_controller#_copyShareLink. On a share there is only one
    // sensible url and this is it.
    if (ctx.onCopyShareLink) actions.appendChild(action("Copy a link to share", ctx.onCopyShareLink))
    else if (ctx.onCopyLink) actions.appendChild(action("Copy link", ctx.onCopyLink))
    if (ctx.onClearPath) actions.appendChild(action("Back to the tree", ctx.onClearPath))
    if (actions.childElementCount) head.appendChild(actions)
    return head
}

function action(label, onClick) {
    let button = document.createElement("button")
    button.type = "button"
    button.className = "ft-rel-action"
    button.textContent = label
    button.addEventListener("click", (event) => {
        event.preventDefault()
        onClick()
    })
    return button
}

// "Eighth cousin" describes one person; a chart of two describes both.
// Deliberately narrow: only the symmetrical relationships pluralise, because
// "grandmother and grandson" are not two of the same thing.
const SYMMETRICAL = /(cousin|sibling|brother|sister|partner|wife|husband|spouse)$/i
function plural(label) {
    return SYMMETRICAL.test(label) ? `${label}s` : label
}

// Two lines of descent from the ancestor, side by side. The two halves are
// aligned from the TOP, because a generation is a row: the people level with
// each other are the same number of steps from the ancestor they share, which
// is the fact a cousin relationship is made of.
function twoLines(path, pivotIndex, ctx, data) {
    let body = document.createElement("div")
    body.className = "ft-rel-body"

    let ancestor = path[pivotIndex]
    body.appendChild(person(ancestor, ctx, { role: "ancestor" }))

    let fork = document.createElement("div")
    fork.className = "ft-rel-fork"
    fork.setAttribute("aria-hidden", "true")
    body.appendChild(fork)

    // Left runs back up the path to the first person; right runs on to the
    // second. Both start one step below the ancestor.
    let left = path.slice(0, pivotIndex).reverse()
    let right = path.slice(pivotIndex + 1)

    // `lead` on both: the fork's own legs reach down to the first person in
    // each column, so a stub above them would be a second line beside the one
    // already there.
    // What "half" means, written the way this chart writes everything else:
    // drawn, not explained. Two different spouses named side by side under
    // one ancestor IS the reason the pair are half cousins, and a reader sees
    // it in the shape without being told. The sentence this replaces said the
    // same thing in two lines of prose above a diagram that was already
    // making the point.
    let half = data && data.half_via
    let columns = document.createElement("div")
    columns.className = "ft-rel-columns"
    columns.appendChild(column(left, ctx, { lead: true, branch: half ? data.half_via.from : null }))
    columns.appendChild(column(right, ctx, { lead: true, branch: half ? data.half_via.to : null }))
    body.appendChild(columns)
    return body
}

const named = (person) => `${person.name}${person.lifespan ? ` (${person.lifespan})` : ""}`

// Two families joined by marriage, drawn as a family tree - cards all the way.
//
// The chain has no common ancestor, but it is made of families that do: each
// place where it climbs to someone and comes back down is a fork, exactly the
// shape of a cousin chart, and each marriage sets two of those families side
// by side. Pieter Gunst and Jo Nys are three of them - Isabella Willems's
// (Pieter's line and her daughter Amelia), Franciscus Meulemeester's (his
// son, Amelia's husband, and his daughter Francisca) and Pierre D'hondt's
// (down to Eduardus Miny, who married Francisca's daughter, and down to Jo).
//
// Laid out on a grid read straight from the path: a row per generation
// (a step to a parent is a row up, to a child a row down, a marriage stays on
// the row), and a new column at every marriage and every fork. A fork's
// ancestor sits centred over its two branches. The lines are drawn once the
// cards have their real size, with the same elbows the tree uses.
function familyChart(path, ctx) {
    let rows = [0]
    let cols = [0]
    let spans = new Map()
    let col = 0
    for (let index = 1; index < path.length; index++) {
        let step = path[index].step
        if (step === "side") {
            col += 1
            rows[index] = rows[index - 1]
        } else if (step === "up") {
            rows[index] = rows[index - 1] - 1
        } else {
            rows[index] = rows[index - 1] + 1
            // Climbed to here and now going down: a fork. The ancestor spans
            // the line that came up and the new one going down.
            if (path[index - 1].step === "up") {
                spans.set(index - 1, [col, col + 1])
                col += 1
            }
        }
        cols[index] = col
    }
    let top = Math.min(...rows)

    let scroller = document.createElement("div")
    scroller.className = "ft-rel-family-scroll"
    let grid = document.createElement("div")
    grid.className = "ft-rel-family"
    grid.style.setProperty("--ft-rel-cols", String(col + 1))
    let lines = document.createElementNS("http://www.w3.org/2000/svg", "svg")
    lines.classList.add("ft-rel-family-lines")
    lines.setAttribute("aria-hidden", "true")
    grid.appendChild(lines)

    let cells = path.map((entry, index) => {
        let span = spans.get(index)
        let role = index === 0 || index === path.length - 1 ? "end" : span ? "ancestor" : "step"
        let slot = person(entry, ctx, { role })
        slot.classList.add("ft-rel-family-cell")
        slot.style.gridRow = String(rows[index] - top + 1)
        slot.style.gridColumn = span ? `${span[0] + 1} / span 2` : String(cols[index] + 1)
        if (span) slot.classList.add("ft-rel-family-cell--fork")
        grid.appendChild(slot)
        return slot
    })

    let marks = []
    let draw = () => {
        lines.replaceChildren()
        marks.forEach((mark) => mark.remove())
        marks = []
        lines.setAttribute("width", String(grid.scrollWidth))
        lines.setAttribute("height", String(grid.scrollHeight))
        for (let index = 1; index < path.length; index++) {
            let a = box(cells[index - 1])
            let b = box(cells[index])
            if (path[index].step === "side") {
                let [left, right] = a.x < b.x ? [a, b] : [b, a]
                let y = (left.cy + right.cy) / 2
                lines.appendChild(segment(`M ${left.right} ${y} H ${right.left}`, "ft-rel-family-marriage"))
                let ring = document.createElement("span")
                ring.className = "ft-rel-family-ring"
                ring.textContent = "⚭"
                ring.title = "Married"
                ring.style.left = `${(left.right + right.left) / 2}px`
                ring.style.top = `${y}px`
                grid.appendChild(ring)
                marks.push(ring)
            } else {
                let [parent, child] = path[index].step === "up" ? [b, a] : [a, b]
                let mid = (parent.bottom + child.top) / 2
                lines.appendChild(segment(`M ${parent.cx} ${parent.bottom} V ${mid} H ${child.cx} V ${child.top}`, "ft-rel-family-descent"))
            }
        }
    }
    // Fit the width when it can be done legibly - a chain across three
    // families is wider than a laptop's canvas at full size, and seeing all of
    // it at once is the point. Below READABLE_SCALE it stops shrinking and the
    // page scrolls sideways instead (a phone), because a chart you can see
    // but not read has lost the argument it was drawn to make.
    let sizer = document.createElement("div")
    sizer.className = "ft-rel-family-sizer"
    let fit = () => {
        let natural = grid.offsetWidth
        let available = scroller.clientWidth
        if (!natural || !available) return
        let scale = Math.min(1, Math.max(READABLE_SCALE, available / natural))
        grid.style.transform = scale < 1 ? `scale(${scale})` : ""
        sizer.style.width = `${natural * scale}px`
        sizer.style.height = `${grid.offsetHeight * scale}px`
    }
    let layout = () => {
        draw()
        fit()
    }
    let observer = typeof ResizeObserver === "function" ? new ResizeObserver(() => layout()) : null
    if (observer) {
        observer.observe(grid)
        observer.observe(scroller)
    } else {
        requestAnimationFrame(layout)
    }

    sizer.appendChild(grid)
    scroller.appendChild(sizer)
    return { element: scroller, cleanup: () => observer?.disconnect() }
}

const READABLE_SCALE = 0.72

// A card's edges in the grid's own coordinates (the grid is the offset parent).
function box(el) {
    let card = el.firstElementChild || el
    let width = card.offsetWidth || el.offsetWidth
    let height = card.offsetHeight || el.offsetHeight
    let x = el.offsetLeft + (el.offsetWidth - width) / 2
    let top = el.offsetTop
    return { x, left: x, right: x + width, top, bottom: top + height, cx: x + width / 2, cy: top + height / 2 }
}

function segment(d, className) {
    let el = document.createElementNS("http://www.w3.org/2000/svg", "path")
    el.setAttribute("d", d)
    el.setAttribute("class", className)
    return el
}

// A straight line of descent - no fork, because there is nothing to fork.
function oneLine(path, ctx) {
    let body = document.createElement("div")
    body.className = "ft-rel-body"
    let col = column(path, ctx, { lead: true })
    col.classList.add("ft-rel-column--single")
    body.appendChild(col)
    return body
}

function column(people, ctx, { lead = false, branch = null } = {}) {
    let col = document.createElement("ol")
    col.className = "ft-rel-column"
    if (branch) {
        let note = document.createElement("li")
        note.className = "ft-rel-branch"
        note.textContent = `with ${branch}`
        col.appendChild(note)
    }
    people.forEach((entry, index) => {
        let li = document.createElement("li")
        li.className = "ft-rel-step"
        if (index > 0 || !lead) li.classList.add("ft-rel-step--linked")
        li.appendChild(person(entry, ctx, { role: index === people.length - 1 ? "end" : "step" }))
        col.appendChild(li)
    })
    return col
}

// One card renderer for the whole app.
//
// This drew its own centred card for a while and it was a lookalike: no accent,
// no avatar, no relation line, a different click. The tree's card is already
// the house style - photograph or coloured initials, name, who they are, their
// dates - and a chart of the same people should not invent a second way of
// showing one. Reusing it also means this view cannot drift from the others
// later, which a copy always does.
function person(entry, ctx, { role }) {
    let found = ctx.nodesById.get(entry.id)
    let node = found || {}
    let slot = document.createElement("div")
    slot.className = `ft-rel-person ft-rel-person--${role}`

    // Somebody from the connected family's tree has no node in this tree's
    // payload. The answer carries what the tree's card reads - name, sex, how
    // they are related, years - so they get the SAME card, built from it, with
    // no menu (its actions are for this tree's people) and nothing to open.
    if (ctx.renderCard && !found && entry.away) {
        // The real id: person ids are unique across trees, and the page dims
        // every card whose id is not on the traced path.
        let card = ctx.renderCard({
            id: entry.id, name: entry.name, gender: entry.gender, relation: entry.relation,
            birth_year: entry.birth_year, death_year: entry.death_year, deceased: entry.deceased
        })
        card.querySelector(".contact-tree-card-menu-btn")?.remove()
        card.classList.add("contact-tree-card--connected")
        slot.appendChild(card)
        return slot
    }

    if (ctx.renderCard && found) {
        // A card this tree cannot name a relation for (Pierre D'hondt, two
        // marriages from the reader) takes the chart's own: "Jo Nys's 6x
        // great-grandfather".
        let card = ctx.renderCard(node.relation || !entry.relation ? node : { ...node, relation: entry.relation })
        // Only for a node with no panel behind it at all - now that a public
        // share has one too, this is the rare case rather than every visitor.
        if (!node.detail_url) {
            card.addEventListener("click", (event) => {
                event.preventDefault()
                document.dispatchEvent(new CustomEvent("family-tree:focus-person", { detail: { personId: entry.id } }))
            })
        }
        slot.appendChild(card)
        return slot
    }

    // Fallback for a caller with no renderer (the public payload always has
    // one, but the view should not depend on that).
    slot.dataset.nodeId = String(entry.id)
    let card = document.createElement("div")
    card.className = "ft-rel-card"
    let name = document.createElement("span")
    name.className = "ft-rel-name"
    name.textContent = node.name || entry.name
    card.appendChild(name)
    if (entry.relation) {
        let relation = document.createElement("span")
        relation.className = "ft-rel-relation"
        relation.textContent = entry.relation
        card.appendChild(relation)
    }
    let facts = [found ? lifespanLabel(node) : entry.lifespan, node.birth_place].filter(Boolean).join(" · ")
    if (facts) {
        let meta = document.createElement("span")
        meta.className = "ft-rel-meta"
        meta.textContent = facts
        card.appendChild(meta)
    }
    slot.appendChild(card)
    return slot
}

function note(text) {
    let el = document.createElement("p")
    el.className = "ft-rel-note"
    el.textContent = text
    return el
}
